import React from 'react';
import { DateTime } from 'luxon';
import { SmartImportStatus } from './types';
import { defineMessages, useIntl } from 'react-intl';
import { Pill, PillTone } from '../../components/design-system';

export const ACTIVE_IMPORT_STATUSES: SmartImportStatus[] = [
  'awaiting_confirmation',
  'processing',
  'ready_for_review',
  'importing',
];

export const CANCELLABLE_IMPORT_STATUSES: SmartImportStatus[] = [
  'awaiting_confirmation',
  'processing',
  'ready_for_review',
  'failed',
];

export const IMPORT_STATUS_LABELS: Record<SmartImportStatus, string> = {
  analyzing: 'Analyzing',
  awaiting_confirmation: 'Awaiting confirmation',
  processing: 'Processing',
  ready_for_review: 'Ready for review',
  importing: 'Storing',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const importStatusMessages = defineMessages({
  analyzing: { id: 'imports.status.analyzing', defaultMessage: 'Analyzing' },
  awaiting_confirmation: {
    id: 'imports.status.awaitingConfirmation',
    defaultMessage: 'Awaiting confirmation',
  },
  processing: {
    id: 'imports.status.processing',
    defaultMessage: 'Processing',
  },
  ready_for_review: {
    id: 'imports.status.readyForReview',
    defaultMessage: 'Ready for review',
  },
  importing: { id: 'imports.status.storing', defaultMessage: 'Storing' },
  completed: { id: 'imports.status.completed', defaultMessage: 'Completed' },
  failed: { id: 'imports.status.failed', defaultMessage: 'Failed' },
  cancelled: { id: 'imports.status.cancelled', defaultMessage: 'Cancelled' },
});

const STATUS_TONES: Record<SmartImportStatus, PillTone> = {
  analyzing: 'info',
  awaiting_confirmation: 'warning',
  processing: 'info',
  ready_for_review: 'success',
  importing: 'info',
  completed: 'success',
  failed: 'danger',
  cancelled: 'neutral',
};

export function isImportActive(status: SmartImportStatus): boolean {
  return ACTIVE_IMPORT_STATUSES.includes(status);
}

export function isImportCancellable(status: SmartImportStatus): boolean {
  return CANCELLABLE_IMPORT_STATUSES.includes(status);
}

export function formatImportDuration(
  startedAt: string | null,
  completedAt: string | null,
  updatedAt: string,
  status: SmartImportStatus,
  now: number,
  notStartedLabel = 'Not started'
): string {
  if (!startedAt) {
    return notStartedLabel;
  }

  const start = DateTime.fromISO(startedAt).toMillis();
  const terminal = ['completed', 'failed', 'cancelled'].includes(status);
  let end = now;

  if (completedAt) {
    end = DateTime.fromISO(completedAt).toMillis();
  } else if (terminal) {
    end = DateTime.fromISO(updatedAt).toMillis();
  }

  const seconds = Math.max(0, Math.floor((end - start) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  }

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }

  return `${remainingSeconds}s`;
}

interface ImportStatusChipProps {
  status: SmartImportStatus;
}

export const ImportStatusChip = React.memo(
  ({ status }: ImportStatusChipProps) => {
    const intl = useIntl();

    return (
      <Pill tone={STATUS_TONES[status]} variant="outlined">
        {intl.formatMessage(importStatusMessages[status])}
      </Pill>
    );
  }
);
